import {
  ShiftManagementBulkDeleteRestDaysDocument,
  ShiftManagementBulkDeleteShiftsDocument,
} from 'generated/resources/graphql'
import { uniq } from 'lodash'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import shiftManagementCounts from 'modules/shift_management/lib/shiftManagementCounts'
import { trackBulkDeleteEvent } from 'modules/shift_management/lib/trackDeleteEvent'
import type { RestDay, Shift, ShiftsRow } from 'modules/shift_management/lib/types'
import { removeDestroyedRestDaysCache } from 'modules/shift_management/utils/apolloCache/removeDestroyedRestDaysCache'
import { removeDestroyedShiftsCache } from 'modules/shift_management/utils/apolloCache/removeDestroyedShiftsCache'

interface Props {
  shifts: Shift[]
  restDays: RestDay[]
  destroyBackupShifts: boolean
  destroyAutomaticOnly?: boolean
}

export const useDestroyShiftsAndRestDays = ({
  shifts,
  restDays,
  destroyBackupShifts,
  destroyAutomaticOnly = false,
}: Props) => {
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view, shiftsConflicts }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { refreshStats } = useRefreshStats()
  const shiftIds = uniq(shifts.map((shift) => shift.id))
  const restDayIds = restDays.map((restDay) => restDay.id)

  const { employeeCount } = shiftManagementCounts(shifts, restDays)

  const [destroyShifts] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
    update: (cache, { data }) => {
      removeDestroyedShiftsCache({
        cache,
        view,
        shifts: data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts || [],
      })
    },
  })

  const [destroyRestDays] = useMutation(ShiftManagementBulkDeleteRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay,
    update: (cache, { data }) => {
      removeDestroyedRestDaysCache({
        cache,
        view,
        restDays: data?.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays || [],
      })
    },
  })

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      trackBulkDeleteEvent(
        shifts,
        restDays,
        view,
        employeeCount,
        shiftsConflicts,
        'top_bar_button',
        destroyAutomaticOnly
      )

      await destroyRestDays({
        variables: { ids: restDayIds },
      })

      await destroyShifts({
        variables: {
          ids: shiftIds,
          destroyBackupShifts,
          destroyAutomaticShifts: destroyAutomaticOnly,
        },
      })

      // Get shiftsRows where there are affected shifts to update stats
      const shiftKeys = new Set(uniq(shifts.map((shift) => shift.rowKeyReference(view))))
      const restDayKeys = new Set(restDayIds)
      const previousShiftsRowsFromShifts = shiftsRows.filter((row: ShiftsRow) =>
        shiftKeys.has(row.key)
      )
      const previousShiftsRowsFromRestDays = shiftsRows.filter((row: ShiftsRow) =>
        restDayKeys.has(String(row.key))
      )

      const previousShiftsRows = uniq([
        ...previousShiftsRowsFromRestDays,
        ...previousShiftsRowsFromShifts,
      ])

      await refreshStats(previousShiftsRows)
      dispatchSelection({ type: 'CLEAR_SELECTION' })
      closePortal()
    },
    {
      successMessage: `${i18n.t('shift_management.sidepanel.rest_days')} ${i18n.t(
        'shift_management.add_shift.and'
      )} ${i18n.t('shift_management.actions.delete_shifts_success').toLowerCase()}`,
    }
  )

  return {
    performAction,
    submitting,
  }
}
